/**
 * Santé, activité et **croisements** (V2 §6.2, specs §12.5).
 *
 * 🔴 **Posé SOUS l'écran d'activité de l'étape 8, jamais à sa place.** Celui-là
 * montre les quatorze derniers jours, jour par jour, tels qu'ils sont arrivés ;
 * celui-ci les met en série sur la plage choisie. Deux questions différentes —
 * « qu'ai-je fait cette semaine ? » et « comment ça évolue ? » — et c'est
 * pourquoi l'écran de l'étape 8 n'a pas été réécrit : le refaire à l'occasion
 * d'un ajout, c'est le casser à l'occasion d'un ajout.
 *
 * 🔴 **Tout est indicatif et en LECTURE SEULE** (§5.3, specs §7.3). Aucune de
 * ces courbes n'influence un objectif, et la dépense énergétique est posée **à
 * côté** des calories consommées, jamais soustraite : il n'existe nulle part de
 * « restant ajusté ».
 *
 * 🔴 **Les deux croisements sont des NUAGES DE POINTS, sans droite de
 * régression ni coefficient** (§6.2). C'est la forme où la tentation est la plus
 * forte, précisément parce qu'on aimerait qu'elle dise quelque chose.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import type { HealthPointDTO, StatsRange } from '../../../../shared/types';
import { readError } from '../../api/client';
import { fetchHealthStats, healthStatsKey } from '../../api/stats';
import { Card } from '../../components/nourri';
import { Hint } from '../../components/ui/primitives';
import { ReadNote } from '../../components/ui/ReadNote';
import { RangeRow, Scatter, TimeChart } from './StatCharts';

/**
 * Les six séries du §12.5 — « FC de repos, VFC, sommeil, charge et forme,
 * dépense, pas ».
 *
 * 🔴 **Une série par cadre, jamais six courbes superposées.** Elles n'ont ni la
 * même unité ni le même ordre de grandeur : une VFC autour de 80 ms et un compte
 * de pas autour de 12 000 sur la même échelle donneraient cinq lignes plates et
 * une sixième. C'est la règle du second axe, prise par l'autre bout — on
 * n'invente pas d'échelle commune, on empile des cadres.
 */
const SERIES: readonly {
  cle: string;
  titre: string;
  unite: string;
  lire: (point: HealthPointDTO) => number | null;
  formater?: (valeur: number) => string;
}[] = [
  { cle: 'restingHr', titre: 'Fréquence cardiaque au repos', unite: 'bpm', lire: (p) => p.restingHr },
  { cle: 'hrv', titre: 'Variabilité cardiaque', unite: 'ms', lire: (p) => p.hrv },
  {
    cle: 'sleep',
    titre: 'Sommeil',
    unite: 'h',
    lire: (p) => (p.sleepSeconds === null ? null : Math.round((p.sleepSeconds / 3600) * 10) / 10),
    formater: (v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 1 }),
  },
  { cle: 'ctl', titre: 'Charge d’entraînement (CTL)', unite: '', lire: (p) => p.ctl },
  { cle: 'form', titre: 'Forme', unite: '', lire: (p) => p.form },
  { cle: 'expended', titre: 'Dépense énergétique', unite: 'kcal', lire: (p) => p.expendedKcal },
  { cle: 'steps', titre: 'Pas', unite: '', lire: (p) => p.steps },
];

export function HealthStatsSection() {
  const [range, setRange] = useState<StatsRange>('90d');

  const stats = useQuery({
    queryKey: healthStatsKey(range),
    queryFn: () => fetchHealthStats(range),
    placeholderData: (previous) => previous,
  });

  const lecture = readError(stats);
  const data = stats.data;

  if (data === undefined) {
    return (
      <>
        <ReadNote error={lecture} quoi="les statistiques de santé" />
        {lecture === null && <Hint>Chargement…</Hint>}
      </>
    );
  }

  /*
   * ⚠️ **Sans clé, on ne montre pas des cadres vides** : l'écran d'activité
   * au-dessus explique déjà que rien n'est configuré, et répéter six graphiques
   * sans données n'ajouterait qu'du bruit.
   */
  if (!data.configured) return null;

  const dates = data.points.map((p) => p.date);
  const utiles = SERIES.filter((serie) => data.points.some((p) => serie.lire(p) !== null));

  return (
    <div style={stats.isPlaceholderData ? { opacity: 0.45 } : undefined}>
      {/*
        ⚠️ **Sa propre plage**, qui ne gouverne que ce qui suit : la fenêtre de
        quatorze jours du bloc précédent est celle de l'ingestion (§5.2) et ne
        se règle pas. Poser la rangée ici, et non en tête d'onglet, dit
        exactement ce qu'elle commande.
      */}
      <RangeRow range={range} onChange={setRange} />

      {utiles.map((serie) => (
        <Card key={serie.cle}>
          <div className="n-eyebrow">{serie.titre}</div>
          <div className="n-chart">
            <TimeChart
              dates={dates}
              unite={serie.unite}
              formater={serie.formater}
              traces={[
                {
                  libelle: serie.titre,
                  couleur: 'var(--brand)',
                  valeurs: data.points.map(serie.lire),
                },
              ]}
            />
          </div>
        </Card>
      ))}

      <Card>
        <div className="n-eyebrow">Sommeil et RPE moyen</div>
        <div className="n-chart">
          <Scatter
            points={data.sleepVsRpe}
            legendeX="h de sommeil"
            legendeY="RPE moyen"
            formaterX={(v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
            formaterY={(v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}
          />
        </div>
        <p className="n-hint" style={{ marginTop: 10 }}>
          Chaque point est une séance : le sommeil de la nuit précédente en abscisse, le RPE
          moyen de la séance en ordonnée. Aucune droite n’est tracée, et aucun coefficient
          n’est calculé — sur une seule personne et sans période de contrôle, un tel chiffre se
          lirait comme une cause alors qu’il n’en est pas une.
        </p>
      </Card>

      <Card>
        <div className="n-eyebrow">Charge d’entraînement et tendance de poids</div>
        <div className="n-chart">
          <Scatter
            points={data.loadVsWeightTrend}
            legendeX="charge / semaine"
            legendeY="kg/sem."
            formaterY={(v) => v.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
          />
        </div>
        <p className="n-hint" style={{ marginTop: 10 }}>
          Une semaine par point : la charge cumulée en abscisse, la tendance de poids mesurée à
          la fin de cette semaine-là en ordonnée. Les semaines où l’historique de pesée est trop
          court pour qu’une tendance veuille dire quelque chose n’ont pas de point — un zéro y
          serait une mesure inventée.
        </p>
      </Card>
    </div>
  );
}
